import { useTranslation } from 'react-i18next';

import generateHash from '../utils/generateHash';
import { filterByTypename } from '../utils/graphql';
import useBasePageQuery from './useBasePageQuery';
import { usePageMeta } from './usePageMeta';
import { usePageOwner } from './usePageOwner';

interface SharePageUrls {
  facebook: { url: string };
  twitter: { url: string; message: string };
  whatsApp: { message: string };
  sms: { message: string };
  email: {
    subject: string;
    body: string;
  };
}

type MessageOverrideFunc = (shareUrl: string) => string;

interface IMessageOverrides {
  whatsApp: MessageOverrideFunc;
  twitter: MessageOverrideFunc;
  sms: MessageOverrideFunc;
  email: (
    shareUrl: string,
  ) => {
    subject: string;
    body: string;
  };
}

export type MessageOverrides = Partial<IMessageOverrides>;

export function useShareUrls(
  shareUrl?: string,
  messageOverrides?: MessageOverrides,
): SharePageUrls | null {
  const { isPageOwner } = usePageOwner();
  const { data } = useBasePageQuery();
  const { t } = useTranslation('common', { useSuspense: false });
  const { currentUrl, pageType, pageShortName } = usePageMeta();

  if (!data?.page) {
    return null;
  }

  const charities = filterByTypename(
    data.page.relationships?.beneficiaries?.nodes ?? [],
    'Charity',
  );

  const charityName = charities[0]?.name
    ? charities[0].name
    : t('charity.defaultName');

  let _shareUrl = shareUrl ?? currentUrl;
  if (_shareUrl.includes('?')) {
    _shareUrl = `${_shareUrl}&`;
  } else {
    _shareUrl = `${_shareUrl}?`;
  }

  const facebookUrl = `${_shareUrl}utm_source=facebook&utm_medium=${pageType}&utm_content=${pageShortName}&utm_campaign=pfp-share&utm_term=${generateHash()}`;

  const twitterUrl = `${_shareUrl}utm_source=Twitter&utm_medium=${pageType}&utm_content=${pageShortName}&utm_campaign=pfp-tweet&utm_term=${generateHash()}`;
  const getTwitterMessage = () => {
    if (messageOverrides?.twitter) {
      return messageOverrides.twitter(twitterUrl);
    }

    return isPageOwner
      ? t('fundraising.shareTwitterPageOwner', {
          charityName,
        })
      : t('fundraising.shareTwitterNonPageOwner', {
          name: data.page?.owner?.name,
          charityName,
        });
  };

  const emailUrl = `${_shareUrl}utm_source=Sharethis&utm_medium=${pageType}&utm_content=${pageShortName}&utm_campaign=pfp-email&utm_term=${generateHash()}`;
  const getEmailContent = () => {
    if (messageOverrides?.email) {
      return messageOverrides.email(emailUrl);
    }

    const body = isPageOwner
      ? t('fundraising.shareEmailPageOwner', {
          charityName,
          emailUrl,
        })
      : t('fundraising.shareEmailNonPageOwner', {
          name: data.page?.owner?.name,
          charityName,
          emailUrl,
        });

    return {
      subject: t('common.defaultEmailSubject'),
      body,
    };
  };

  const whatsappAndSmsMessage = isPageOwner
    ? t('fundraising.shareWhatsappSMSPageOwner', {
        charityName,
      })
    : t('fundraising.shareWhatsappSMSNonPageOwner', {
        name: data.page.owner?.name,
        charityName,
      });

  const whatsAppUrl = `${_shareUrl}utm_source=whatsapp&utm_medium=${pageType}&utm_content=${pageShortName}&utm_campaign=pfp-whatsapp&utm_term=${generateHash()}`;
  const getWhatsAppMessage = () => {
    if (messageOverrides?.whatsApp) {
      return messageOverrides.whatsApp(whatsAppUrl);
    }

    return `${whatsappAndSmsMessage} ${whatsAppUrl}`;
  };

  const smsUrl = `${_shareUrl}utm_source=sms&utm_medium=${pageType}&utm_content=${pageShortName}&utm_campaign=pfp-sms&utm_term=${generateHash()}`;
  const getSmsMessage = () => {
    if (messageOverrides?.sms) {
      return messageOverrides.sms(smsUrl);
    }

    return `${whatsappAndSmsMessage} ${smsUrl}`;
  };

  return {
    facebook: { url: facebookUrl },
    twitter: {
      url: twitterUrl,
      message: getTwitterMessage(),
    },
    email: getEmailContent(),
    whatsApp: {
      message: getWhatsAppMessage(),
    },
    sms: {
      message: getSmsMessage(),
    },
  };
}
